import { applicationChangeApi } from 'app/services/api';
import _ from 'lodash';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { FeatureFlag } from 'constants/featureFlags';
import {
  LOAN_APPLICATION_ACTION_NAME_PREFIX,
  LoanChangesHistoryState,
} from 'constants/loanApplication';
import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { getUuid, selectIsClaimAllowed, selectIsSubCompanyUser } from 'store/auth/selectors';
import { selectFeatureFlagByKey } from 'store/global/selectors';

export const LOAN_CHANGES_HISTORY_ACTION_TYPE = createNamedActions(
  // LOAN_APPLICATION_ACTION_NAME_PREFIX is used for Redux Middleware in Loan Editor
  `${LOAN_APPLICATION_ACTION_NAME_PREFIX}/loanChangesHistory`,
  {
    GET_CHANGES_HISTORY: 'GET_CHANGES_HISTORY',
    GET_LOG_CHANGES_HISTORY: 'GET_LOG_CHANGES_HISTORY',
    UPDATE_ENTITIES_CHANGES_HISTORY: 'UPDATE_ENTITIES_CHANGES_HISTORY',
    DELETE_CHANGES_HISTORY: 'DELETE_CHANGES_HISTORY',
    CLEAN_PROTECTED_ENTITIES_CHANGES_HISTORY: 'CLEAN_PROTECTED_ENTITIES_CHANGES_HISTORY',
  },
);

const normalizeGetLoanChangesHistory = data =>
  data.reduce((acc, { properties, ...rest }) => {
    properties.forEach(prop => {
      if (!acc[rest.entityPrimaryKey]) {
        acc[rest.entityPrimaryKey] = [];
      }

      acc[rest.entityPrimaryKey].push({
        ...prop,
        ...rest,
        propertyName: _.camelCase(prop.propertyName),
      });
    });

    return acc;
  }, {});

const normalizeUpdateLoanChangesHistory = data =>
  data?.reduce((acc, { properties, ...rest }) => {
    properties.forEach(prop =>
      acc.push({ ...prop, ...rest, propertyName: _.camelCase(prop.propertyName) }),
    );

    return acc;
  }, []);

export const getApplicationChangesHistoryAction = catchAsyncActionError(
  actionData => async (dispatch, getState) => {
    const state = getState();
    const userId = getUuid(state);
    const hasUserPermissionToSaveOtherUsersLoanChanges = selectIsClaimAllowed(
      COMPOSED_CLAIMS.LOAN_APPLICATION_SAVE_OTHER_USER_CHANGES,
    )(state);
    const isPartialPendingChangesEnabled = selectFeatureFlagByKey(
      FeatureFlag.EnablePartialPendingChanges,
    )(state);
    const isPendingChangesByUserEnabled = selectFeatureFlagByKey(FeatureFlag.PendingChangesByUser)(
      state,
    );
    // A subtree-scoped TPO broker is not authorized for changes history and legitimately 403s
    // here; tolerate it silently for them while employees keep an unexpected-failure toast
    // (DEV-4226).
    const isSubCompanyUser = selectIsSubCompanyUser(state);

    const { data } = await applicationChangeApi.getApplicationChangesHistory({
      createdByUserId:
        hasUserPermissionToSaveOtherUsersLoanChanges ||
        isPartialPendingChangesEnabled ||
        isPendingChangesByUserEnabled
          ? null
          : userId,
      historyState: LoanChangesHistoryState.PENDING,
      skipForbiddenError: isSubCompanyUser,
      ...actionData,
    });

    return dispatch({
      type: LOAN_CHANGES_HISTORY_ACTION_TYPE.GET_CHANGES_HISTORY,
      payload: normalizeGetLoanChangesHistory(data),
    });
  },
);

export const getApplicationLogChangesHistoryAction = catchAsyncActionError(
  params => async (dispatch, getState) => {
    const isSubCompanyUser = selectIsSubCompanyUser(getState());

    const { data } = await applicationChangeApi.getApplicationChangesHistory({
      skipForbiddenError: isSubCompanyUser,
      ...params,
    });

    return dispatch({
      type: LOAN_CHANGES_HISTORY_ACTION_TYPE.GET_LOG_CHANGES_HISTORY,
      payload: normalizeGetLoanChangesHistory(data),
    });
  },
);

export const updateLoanChangesHistoryAction = ({ metadata } = {}, isProtected = false) => ({
  type: LOAN_CHANGES_HISTORY_ACTION_TYPE.UPDATE_ENTITIES_CHANGES_HISTORY,
  payload: { changes: normalizeUpdateLoanChangesHistory(metadata?.historyIncrement), isProtected },
});

export const cleanProtectedLoanChangesHistoryAction = () => ({
  type: LOAN_CHANGES_HISTORY_ACTION_TYPE.CLEAN_PROTECTED_ENTITIES_CHANGES_HISTORY,
});

export const deleteLoanChangesHistoryAction = () => ({
  type: LOAN_CHANGES_HISTORY_ACTION_TYPE.DELETE_CHANGES_HISTORY,
});
